/**
 * Microphone → 16 kHz mono Int16 PCM in 100 ms chunks. Uses an AudioWorklet (public/pcm-processor.js)
 * and falls back to a ScriptProcessorNode where worklets are unavailable. Chunks are handed to the
 * caller and dropped; nothing is buffered or stored.
 */
export const PCM_SAMPLE_RATE = 16000;
export const PCM_CHUNK_MS = 100;

export interface PcmCapture {
  /** Disconnects the graph and suspends the shared context. Idempotent. Audio stops leaving the browser immediately. */
  stop(): Promise<void>;
  readonly kind: 'worklet' | 'script-processor';
}

export type PcmChunkHandler = (pcm: Int16Array) => void;

interface WindowWithWebkitAudio extends Window {
  webkitAudioContext?: typeof AudioContext;
}

function getAudioContextCtor(): typeof AudioContext | undefined {
  if (typeof AudioContext !== 'undefined') return AudioContext;
  return (window as WindowWithWebkitAudio).webkitAudioContext;
}

function downsampleToInt16(input: Float32Array, inputRate: number, outRate: number, carry: { pos: number }): Int16Array {
  const ratio = inputRate / outRate;
  if (ratio <= 1.0001) {
    const out = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i] ?? 0));
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    return out;
  }
  const samples: number[] = [];
  let pos = carry.pos;
  const n = input.length;
  while (pos < n) {
    const start = Math.floor(pos);
    const end = Math.min(n, Math.floor(pos + ratio));
    let sum = 0;
    let count = 0;
    for (let i = start; i < end; i++) {
      sum += input[i] ?? 0;
      count++;
    }
    const s = Math.max(-1, Math.min(1, count > 0 ? sum / count : 0));
    samples.push(s < 0 ? s * 0x8000 : s * 0x7fff);
    pos += ratio;
  }
  carry.pos = pos - n;
  return Int16Array.from(samples);
}

/**
 * One AudioContext for the whole session. iOS Safari limits the number of contexts, only lets a context start
 * (resume) from a user gesture, and misbehaves when a context is created with a non-hardware sample rate. So:
 * create it once at the hardware rate (the worklet resamples to 16 kHz), resume it inside the tap/keypress
 * via primeAudioContext(), and suspend rather than close it between captures.
 */
let sharedContext: AudioContext | undefined;
let workletLoadedFor: AudioContext | undefined;

function getSharedContext(): AudioContext | undefined {
  const Ctor = getAudioContextCtor();
  if (!Ctor) return undefined;
  if (!sharedContext || sharedContext.state === 'closed') {
    sharedContext = new Ctor();
    workletLoadedFor = undefined;
  }
  return sharedContext;
}

/** Call synchronously from the user gesture that starts listening (mic button, key press). Safe to call often. */
export function primeAudioContext(): void {
  const ctx = getSharedContext();
  if (ctx && ctx.state !== 'running') void ctx.resume().catch(() => {});
}

export async function startPcmCapture(stream: MediaStream, onChunk: PcmChunkHandler): Promise<PcmCapture> {
  const ctx = getSharedContext();
  if (!ctx) throw new Error('Web Audio is not available in this browser');
  if (ctx.state !== 'running') {
    try {
      await ctx.resume();
    } catch {
      /* some browsers resume lazily */
    }
  }
  const source = ctx.createMediaStreamSource(stream);
  let stopped = false;
  let teardown: () => void = () => {};

  const stop = async (): Promise<void> => {
    if (stopped) return;
    stopped = true;
    try {
      teardown();
      source.disconnect();
    } catch {
      /* ignore */
    }
    // Keep the context alive (suspended) so the next capture can resume it from a gesture instantly.
    try {
      if (ctx.state === 'running') await ctx.suspend();
    } catch {
      /* ignore */
    }
  };

  const workletSupported = typeof AudioWorkletNode !== 'undefined' && ctx.audioWorklet !== undefined;
  if (workletSupported) {
    try {
      if (workletLoadedFor !== ctx) {
        await ctx.audioWorklet.addModule(`${import.meta.env.BASE_URL}pcm-processor.js`);
        workletLoadedFor = ctx;
      }
      const node = new AudioWorkletNode(ctx, 'pcm-processor', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
        processorOptions: { targetSampleRate: PCM_SAMPLE_RATE, chunkMs: PCM_CHUNK_MS },
      });
      node.port.onmessage = (e: MessageEvent<{ type?: string; samples?: Int16Array }>) => {
        if (stopped) return;
        if (e.data?.type === 'pcm' && e.data.samples) onChunk(e.data.samples);
      };
      source.connect(node);
      teardown = () => {
        node.port.postMessage({ type: 'stop' });
        node.port.onmessage = null;
        node.disconnect();
      };
      return { stop, kind: 'worklet' };
    } catch {
      /* fall through to ScriptProcessorNode */
    }
  }

  // Fallback: ScriptProcessorNode (deprecated but universally available).
  const chunkSamples = Math.round((PCM_SAMPLE_RATE * PCM_CHUNK_MS) / 1000);
  const processor = ctx.createScriptProcessor(4096, 1, 1);
  const carry = { pos: 0 };
  let pending = new Int16Array(chunkSamples);
  let filled = 0;
  processor.onaudioprocess = (ev: AudioProcessingEvent) => {
    if (stopped) return;
    const input = ev.inputBuffer.getChannelData(0);
    const pcm = downsampleToInt16(input, ctx.sampleRate, PCM_SAMPLE_RATE, carry);
    let offset = 0;
    while (offset < pcm.length) {
      const take = Math.min(chunkSamples - filled, pcm.length - offset);
      pending.set(pcm.subarray(offset, offset + take), filled);
      filled += take;
      offset += take;
      if (filled === chunkSamples) {
        onChunk(pending);
        pending = new Int16Array(chunkSamples);
        filled = 0;
      }
    }
  };
  // Silent sink so the processor is pulled by the graph without being audible.
  const sink = ctx.createGain();
  sink.gain.value = 0;
  source.connect(processor);
  processor.connect(sink);
  sink.connect(ctx.destination);
  teardown = () => {
    processor.onaudioprocess = null;
    processor.disconnect();
    sink.disconnect();
  };
  return { stop, kind: 'script-processor' };
}

/** Stops every track of a MediaStream (the microphone light goes off). */
export function stopStreamTracks(stream: MediaStream | undefined): void {
  if (!stream) return;
  for (const track of stream.getTracks()) {
    try {
      track.stop();
    } catch {
      /* ignore */
    }
  }
}
